import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './GamePage.css';

const ROWS = 4;
const COLS = 5;
const EMPTY = 0;
const PLAYER = 1;
const COMPUTER = 2;
const BTK_WINNER = 3;
const GAME_TIME_LIMIT = 120000; // 2 minutes

const GamePage = () => {
  const navigate = useNavigate();
  const [grid, setGrid] = useState(() =>
    Array(ROWS).fill().map(() => Array(COLS).fill(EMPTY))
  );
  const [currentPlayer, setCurrentPlayer] = useState(PLAYER);
  const [gameStatus, setGameStatus] = useState('playing'); // 'playing', 'won', 'timeout'
  const [timeLeft, setTimeLeft] = useState(GAME_TIME_LIMIT);
  const [winningCells, setWinningCells] = useState([]);

  // Timer effect
  useEffect(() => {
    if (gameStatus !== 'playing') return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1000) {
          setGameStatus('timeout');
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameStatus]);

  // Navigate to result page when game ends
  useEffect(() => {
    if (gameStatus === 'won' || gameStatus === 'timeout') {
      const timeout = setTimeout(() => {
        navigate('/result');
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [gameStatus, navigate]);

  // Computer move after player
  useEffect(() => {
    if (currentPlayer === COMPUTER && gameStatus === 'playing') {
      const timeout = setTimeout(() => {
        makeComputerMove();
      }, 1500);
      return () => clearTimeout(timeout);
    }
  }, [currentPlayer, gameStatus]);

  const checkWin = useCallback((grid, row, col, player) => {
    const directions = [
      [0, 1],   // horizontal
      [1, 0],   // vertical
      [1, 1],   // diagonal /
      [1, -1]   // diagonal \
    ];

    for (let [dr, dc] of directions) {
      let count = 1;
      let cells = [[row, col]];

      // Check forward direction
      for (let i = 1; i < 4; i++) {
        const newRow = row + dr * i;
        const newCol = col + dc * i;
        if (newRow >= 0 && newRow < ROWS && newCol >= 0 && newCol < COLS &&
          grid[newRow][newCol] === player) {
          count++;
          cells.push([newRow, newCol]);
        } else {
          break;
        }
      }

      // Check backward direction
      for (let i = 1; i < 4; i++) {
        const newRow = row - dr * i;
        const newCol = col - dc * i;
        if (newRow >= 0 && newRow < ROWS && newCol >= 0 && newCol < COLS &&
          grid[newRow][newCol] === player) {
          count++;
          cells.unshift([newRow, newCol]);
        } else {
          break;
        }
      }

      if (count >= 4) {
        return cells.slice(0, 4);
      }
    }
    return null;
  }, []);

  const makeMove = useCallback((row, col, player) => {
    setGrid(prevGrid => {
      const newGrid = prevGrid.map(row => [...row]);

      // Place piece at the specific clicked position (only if cell is empty)
      if (newGrid[row][col] === EMPTY) {
        newGrid[row][col] = player;

        // Check for win condition
        const winCells = checkWin(newGrid, row, col, player);
        if (winCells && player === PLAYER) {
          // Mark the 4th cell as BTK winner for special styling
          newGrid[row][col] = BTK_WINNER;
          setWinningCells(winCells);
          setGameStatus('won');
        }
      }

      return newGrid;
    });
  }, [checkWin]);

  const makeComputerMove = useCallback(() => {
    // Find all empty cells for computer to choose from
    const availableCells = [];
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        if (grid[row][col] === EMPTY) {
          availableCells.push({ row, col });
        }
      }
    }

    if (availableCells.length > 0) {
      const randomCell = availableCells[Math.floor(Math.random() * availableCells.length)];
      makeMove(randomCell.row, randomCell.col, COMPUTER);
      setCurrentPlayer(PLAYER);
    }
  }, [grid, makeMove]);

  const handleCellClick = (row, col) => {
    if (currentPlayer !== PLAYER || gameStatus !== 'playing') return;
    if (grid[row][col] !== EMPTY) return; // Cell already occupied

    makeMove(row, col, PLAYER);
    setCurrentPlayer(COMPUTER);
  };

  const getCellClass = (row, col) => {
    const cell = grid[row][col];
    let className = 'grid-cell';

    if (cell === PLAYER) className += ' player-cell';
    else if (cell === COMPUTER) className += ' computer-cell';
    else if (cell === BTK_WINNER) className += ' btk-winner-cell';

    if (winningCells.some(([r, c]) => r === row && c === col)) {
      className += ' winning-cell';
    }

    return className;
  };

  return (
    <div className="game-page">


      <div className="game-layout">
        {/* Left side - Game Grid Container (matching image design) */}
        <div className="game-section">
          <div className="grid-container">
            <div className="game-grid">
              {grid.map((row, rowIndex) =>
                row.map((cell, colIndex) => (
                  <motion.div
                    key={`${rowIndex}-${colIndex}`}
                    className={getCellClass(rowIndex, colIndex)}
                    onClick={() => handleCellClick(rowIndex, colIndex)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: (rowIndex * COLS + colIndex) * 0.05 }}
                  >
                    <AnimatePresence>

                      {cell === BTK_WINNER && (
                        <motion.div
                          className="btk-text"
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.5, duration: 0.8 }}
                        >
                          BTK
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right side - Text content (matching image layout exactly) */}
        <div className="instructions-section">
          <motion.div
            className="instructions-content"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
          >


            <img src="/assets/game_ru.png" alt="Instructions" className="instructions-image" />
            <img src="/assets/game_rb.png" alt="Instructions" className="instructions-image" />


          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default GamePage;